import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Database, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialMode = new URLSearchParams(location.search).get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ org_name: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between brand-ink text-white p-12 relative overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 brand-grid pointer-events-none" />
        <div className="flex items-center gap-2.5 relative z-10">
          <span className="w-9 h-9 rounded-xl bg-[#C2410C] flex items-center justify-center shadow-glow">
            <Database size={20} weight="fill" className="text-white" />
          </span>
          <span className="font-head font-bold text-xl">Inspiro Interface</span>
        </div>
        <div className="relative z-10">
          <h2 className="font-head font-black text-4xl leading-tight tracking-tight">
            Enterprise data<br />migration, without<br />the point-to-point mess.
          </h2>
          <p className="text-zinc-300 mt-6 max-w-md text-sm leading-relaxed">
            A connector-based platform to discover, map, transform, validate and migrate
            records between Salesforce, HubSpot, CSV and beyond — with full tenant isolation
            and audit trails.
          </p>
          <div className="flex gap-6 mt-10 text-xs uppercase tracking-[0.2em] text-zinc-400">
            <span>Multi-Tenant</span><span>Encrypted</span><span>Auditable</span>
          </div>
        </div>
        <div className="relative z-10 text-xs text-zinc-500">© 2026 Inspiro Interface</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8 bg-[#FAFAFA]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Database size={24} weight="fill" /><span className="font-head font-bold text-lg">Inspiro Interface</span>
          </div>
          <h1 className="font-head font-bold text-2xl tracking-tight text-stone-950">
            {mode === "login" ? "Sign in" : "Create your organization"}
          </h1>
          <p className="text-sm text-stone-500 mt-1 mb-8">
            {mode === "login" ? "Access your migration workspace." : "Start a new tenant workspace."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Organization</Label>
                  <Input data-testid="reg-org" value={form.org_name} onChange={set("org_name")}
                    required className="mt-1 rounded-xl" placeholder="Acme Corp" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Full name</Label>
                  <Input data-testid="reg-name" value={form.name} onChange={set("name")}
                    required className="mt-1 rounded-xl" placeholder="Jane Doe" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">Email</Label>
              <Input data-testid="login-email" type="email" value={form.email} onChange={set("email")}
                required className="mt-1 rounded-xl" placeholder="you@company.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-stone-500">Password</Label>
                {mode === "login" && (
                  <button type="button" data-testid="forgot-link"
                    onClick={() => navigate("/forgot-password")}
                    className="text-xs text-stone-500 hover:text-[#C2410C] transition-colors duration-200">
                    Forgot password?
                  </button>
                )}
              </div>
              <PasswordInput testid="login-password" value={form.password} onChange={set("password")}
                required className="mt-1 rounded-xl" placeholder="••••••••" />
            </div>
            {error && <div data-testid="auth-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl">{error}</div>}
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200 gap-2">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create workspace"}
              <ArrowRight size={16} />
            </Button>
          </form>

          <button
            data-testid="toggle-auth-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-6 text-sm text-stone-500 hover:text-[#C2410C] transition-colors duration-200"
          >
            {mode === "login" ? "Need an account? Create organization →" : "Already have an account? Sign in →"}
          </button>
        </div>
      </div>
    </div>
  );
}
